import {
  ReportAggregation,
  ReportColumn,
  ReportDefinition,
  ReportDynamicFilter,
  ReportFieldType,
  ReportFilterOperator,
  ReportMetadataFilter,
  ReportSort,
} from '../models/report.model';
import { resolveTimeRange } from './report-field.utils';

/**
 * Pure translation of a {@link ReportDefinition} into a parameterised SQL query
 * against the local `interaction_records` table.
 *
 * Design notes:
 * - Everything is parameterised (including JSON paths via `json_extract(data, ?)`)
 *   so no user-supplied string is ever concatenated into SQL — safe by
 *   construction.
 * - Detail columns are aliased positionally (`c0`, `c1`, …) so the caller maps
 *   rows back to {@link ReportColumn} definitions without quoting concerns.
 * - Aggregate queries return a single row aliased `agg_value`.
 *
 * Phase 1 scope: single-form reports; `count` and `sum` aggregations; no
 * group-by; no latest-per-instance reduction.
 */

export interface BuiltReportQuery {
  sql: string;
  params: unknown[];
  /** True when the query is an aggregate (`agg_value` column). */
  isAggregate: boolean;
  /** True when the query is a grouped aggregate (`group_key` + `agg_value` rows). */
  isGrouped: boolean;
}

/** Maps a metadata field name to its physical column on `interaction_records`. */
const METADATA_COLUMN: Record<string, string> = {
  checkpoint: 'asset_id',
  form: 'interaction_type_id',
  asset_instance: 'asset_instance_id',
  created_by: 'created_by',
  created_at: 'created_at',
};

/**
 * Validates a JSON field key so path construction stays predictable. Form field
 * keys are ids from the nanoid / UUID alphabet, so hyphens are allowed. The key
 * is embedded in a double-quoted JSON path (`$."<key>"`), and this whitelist
 * excludes `"` / `.` / `[` so the quoting can never be broken out of.
 */
function isSafeKey(key: string): boolean {
  return /^[A-Za-z0-9_-]+$/.test(key);
}

/**
 * Returns the SQL expression for a column/filter target, pushing any needed
 * params. Dynamic fields use `json_extract(data, ?)`; numeric dynamic fields are
 * CAST to REAL so comparisons and SUM behave numerically.
 */
function fieldExpr(
  source: 'metadata' | 'dynamic',
  key: string,
  fieldType: ReportFieldType,
  params: unknown[],
): string {
  if (source === 'metadata') {
    const column = METADATA_COLUMN[key];
    if (!column) {
      throw new Error(`Unknown report metadata field: ${key}`);
    }
    return `ir.${column}`;
  }

  if (!isSafeKey(key)) {
    throw new Error(`Unsafe dynamic field key: ${key}`);
  }
  // Double-quote the key so hyphenated / digit-leading ids resolve as object
  // member names rather than being misparsed by the JSON path grammar.
  params.push(`$."${key}"`);
  const extract = `json_extract(ir.data, ?)`;
  return fieldType === 'number' ? `CAST(${extract} AS REAL)` : extract;
}

/** Builds a single WHERE predicate for one filter, pushing params. */
function filterPredicate(
  expr: string,
  fieldType: ReportFieldType,
  operator: ReportFilterOperator,
  value: unknown,
  params: unknown[],
): string {
  switch (operator) {
    case 'eq':
      params.push(sqlScalar(value));
      return `${expr} = ?`;
    case 'neq':
      params.push(sqlScalar(value));
      return `${expr} <> ?`;
    case 'gt':
      params.push(value);
      return `${expr} > ?`;
    case 'gte':
      params.push(value);
      return `${expr} >= ?`;
    case 'lt':
      params.push(value);
      return `${expr} < ?`;
    case 'lte':
      params.push(value);
      return `${expr} <= ?`;
    case 'contains':
      params.push(`%${escapeLike(String(value))}%`);
      return `${expr} LIKE ? ESCAPE '\\'`;
    case 'starts_with':
      params.push(`${escapeLike(String(value))}%`);
      return `${expr} LIKE ? ESCAPE '\\'`;
    case 'in':
    case 'not_in': {
      const list = Array.isArray(value) ? value : [value];
      if (list.length === 0) {
        // An empty IN list matches nothing; NOT IN matches everything.
        return operator === 'in' ? '0 = 1' : '1 = 1';
      }
      list.forEach((v) => params.push(v));
      const placeholders = list.map(() => '?').join(', ');
      return `${expr} ${operator === 'in' ? 'IN' : 'NOT IN'} (${placeholders})`;
    }
    case 'between': {
      const [lo, hi] = Array.isArray(value) ? value : [value, value];
      params.push(lo, hi);
      return `(${expr} >= ? AND ${expr} <= ?)`;
    }
    case 'is_empty':
      return `(${expr} IS NULL OR ${expr} = '')`;
    case 'is_not_empty':
      return `(${expr} IS NOT NULL AND ${expr} <> '')`;
    default:
      throw new Error(`Unsupported report filter operator: ${operator}`);
  }
}

/**
 * json_extract() returns JSON true/false as integer 1/0, so boolean filter
 * values are bound as integers rather than relying on the driver's boolean
 * binding.
 */
function sqlScalar(value: unknown): unknown {
  return typeof value === 'boolean' ? (value ? 1 : 0) : value;
}

function escapeLike(value: string): string {
  return value.replace(/[\\%_]/g, (ch) => `\\${ch}`);
}

/** Builds the shared WHERE clause (scope + filters), pushing params. */
/**
 * Clauses applied BEFORE latest-per-instance reduction: the form scope and the
 * as-of cutoff. These constrain which rows are ranked so the "latest" is the
 * latest within the form (and at/before the as-of timestamp).
 */
function preReductionClauses(def: ReportDefinition, params: unknown[]): string[] {
  const clauses: string[] = [];

  if (def.formScope === 'single_form' && def.formId) {
    params.push(def.formId);
    clauses.push('ir.interaction_type_id = ?');
  }

  if (def.asOfTimestampMs != null) {
    params.push(def.asOfTimestampMs);
    clauses.push('ir.created_at <= ?');
  }

  return clauses;
}

/**
 * Clauses applied AFTER reduction: scope (checkpoints/users/time range) and all
 * user filters. Per design §6.3 these run on the reduced row set so state-style
 * questions ("currently checked out", "checked in this week") work correctly.
 */
function postReductionClauses(def: ReportDefinition, params: unknown[]): string[] {
  const clauses: string[] = [];

  if (def.checkpointIds && def.checkpointIds.length > 0) {
    def.checkpointIds.forEach((id) => params.push(id));
    clauses.push(`ir.asset_id IN (${def.checkpointIds.map(() => '?').join(', ')})`);
  }

  if (def.userIds && def.userIds.length > 0) {
    def.userIds.forEach((id) => params.push(id));
    clauses.push(`ir.created_by IN (${def.userIds.map(() => '?').join(', ')})`);
  }

  const { startMs, endMs } = resolveTimeRange(def.timeRange);
  if (startMs != null) {
    params.push(startMs);
    clauses.push('ir.created_at >= ?');
  }
  if (endMs != null) {
    params.push(endMs);
    clauses.push('ir.created_at <= ?');
  }

  for (const f of def.metadataFilters ?? []) {
    const expr = fieldExpr('metadata', f.field, f.fieldType, params);
    clauses.push(filterPredicate(expr, f.fieldType, f.operator, f.value, params));
  }

  for (const f of def.dynamicFilters ?? []) {
    const expr = fieldExpr('dynamic', f.fieldKey, f.fieldType, params);
    clauses.push(filterPredicate(expr, f.fieldType, f.operator, f.value, params));
  }

  return clauses;
}

function whereFrom(clauses: string[]): string {
  return clauses.length > 0 ? `WHERE ${clauses.join(' AND ')}` : '';
}

/** True when latest-per-checkpoint-instance reduction is enabled. */
function reductionEnabled(def: ReportDefinition): boolean {
  return !!def.latestPerInstance?.enabled;
}

/** Default detail columns used when a report selects none explicitly. */
export const DEFAULT_DETAIL_COLUMNS: ReportColumn[] = [
  { source: 'metadata', key: 'created_at', fieldType: 'date' },
  { source: 'metadata', key: 'checkpoint', fieldType: 'text' },
  { source: 'metadata', key: 'created_by', fieldType: 'text' },
];

/** Returns the effective ordered list of detail columns for a definition. */
export function effectiveColumns(def: ReportDefinition): ReportColumn[] {
  return def.selectedColumns && def.selectedColumns.length > 0
    ? def.selectedColumns
    : DEFAULT_DETAIL_COLUMNS;
}

function buildAggregateSelect(agg: ReportAggregation, params: unknown[]): string {
  if (agg.mode === 'count') {
    return 'COUNT(*) AS agg_value';
  }
  if (!agg.targetKey) {
    throw new Error(`${agg.mode} aggregation requires a target field`);
  }
  const source = agg.targetSource ?? 'dynamic';
  switch (agg.mode) {
    case 'sum':
      return `SUM(${fieldExpr(source, agg.targetKey, 'number', params)}) AS agg_value`;
    case 'avg':
      return `AVG(${fieldExpr(source, agg.targetKey, 'number', params)}) AS agg_value`;
    // min/max/distinct use the raw value so dates/text compare naturally.
    case 'min':
      return `MIN(${fieldExpr(source, agg.targetKey, 'text', params)}) AS agg_value`;
    case 'max':
      return `MAX(${fieldExpr(source, agg.targetKey, 'text', params)}) AS agg_value`;
    case 'distinct_count':
      return `COUNT(DISTINCT ${fieldExpr(source, agg.targetKey, 'text', params)}) AS agg_value`;
    default:
      throw new Error(`Unsupported aggregation mode: ${agg.mode}`);
  }
}

function buildOrderBy(sorts: ReportSort[] | undefined, params: unknown[]): string {
  if (!sorts || sorts.length === 0) {
    return 'ORDER BY ir.created_at DESC';
  }
  const parts = sorts.map((s) => {
    const expr = fieldExpr(s.source, s.key, 'text', params);
    const dir = s.direction === 'asc' ? 'ASC' : 'DESC';
    return `${expr} ${dir}`;
  });
  return `ORDER BY ${parts.join(', ')}`;
}

/**
 * Builds the FROM clause. When latest-per-instance reduction is enabled, wraps
 * `interaction_records` in a `ranked` CTE that keeps the latest N rows per
 * (asset_id, asset_instance_id) — using the pre-reduction clauses (form scope +
 * as-of) to constrain the ranking window.
 *
 * Returns the CTE prefix (may be empty), the FROM source alias `ir`, and the
 * params consumed so the caller can order them correctly.
 */
function buildFrom(def: ReportDefinition): { cte: string; from: string; params: unknown[] } {
  const params: unknown[] = [];
  if (!reductionEnabled(def)) {
    return { cte: '', from: 'interaction_records ir', params };
  }

  const preClauses = preReductionClauses(def, params);
  // The reduction limit (`_rn <= N`) is applied as an inlined literal in the
  // outer query, not here — so no param is pushed for it.
  const cte =
    `WITH ranked AS (` +
    `SELECT ir.*, ROW_NUMBER() OVER (` +
    `PARTITION BY ir.asset_id, ir.asset_instance_id ORDER BY ir.created_at DESC` +
    `) AS _rn FROM interaction_records ir ${whereFrom(preClauses)}` +
    `) `;
  return { cte, from: `ranked ir`, params: [...params] };
}

/**
 * Translates a report definition into a parameterised SQL query.
 *
 * Param order in the returned array always matches the order placeholders appear
 * in the SQL: CTE (pre-reduction) → SELECT → outer WHERE → GROUP BY → ORDER BY.
 */
export function buildReportQuery(def: ReportDefinition): BuiltReportQuery {
  const isAggregate = !!def.aggregation && def.aggregation.mode !== 'none';
  const isGrouped = isAggregate && !!def.groupBy;

  const { cte, from, params: fromParams } = buildFrom(def);
  // When reduction is on, pre-reduction (form/as-of) is inside the CTE; the
  // reduction filter `_rn <= n` gates the outer query. Otherwise all clauses go
  // in the outer WHERE.
  const outerParams: unknown[] = [];
  const outerClauses: string[] = [];
  if (reductionEnabled(def)) {
    outerClauses.push('ir._rn <= ' + Math.max(1, def.latestPerInstance?.n ?? 1));
    outerClauses.push(...postReductionClauses(def, outerParams));
  } else {
    outerClauses.push(...preReductionClauses(def, outerParams));
    outerClauses.push(...postReductionClauses(def, outerParams));
  }
  const where = whereFrom(outerClauses);

  if (isAggregate) {
    const selectParams: unknown[] = [];
    const groupExpr = isGrouped
      ? fieldExpr(def.groupBy!.source, def.groupBy!.key, 'text', selectParams)
      : '';
    const aggSelect = buildAggregateSelect(def.aggregation, selectParams);
    const selectClause = isGrouped ? `${groupExpr} AS group_key, ${aggSelect}` : aggSelect;

    const groupBy = isGrouped ? `GROUP BY group_key` : '';
    const orderBy = isGrouped ? 'ORDER BY group_key' : '';

    const sql = `${cte}SELECT ${selectClause} FROM ${from} ${where} ${groupBy} ${orderBy}`
      .replace(/\s+/g, ' ')
      .trim();
    return {
      sql,
      params: [...fromParams, ...selectParams, ...outerParams],
      isAggregate: true,
      isGrouped,
    };
  }

  const columns = effectiveColumns(def);
  const selectParams: unknown[] = [];
  const selectClause = columns
    .map((col, i) => `${fieldExpr(col.source, col.key, col.fieldType, selectParams)} AS c${i}`)
    .join(', ');

  const orderParams: unknown[] = [];
  const orderBy = buildOrderBy(def.sort, orderParams);

  const sql = `${cte}SELECT ${selectClause} FROM ${from} ${where} ${orderBy}`
    .replace(/\s+/g, ' ')
    .trim();

  return {
    sql,
    params: [...fromParams, ...selectParams, ...outerParams, ...orderParams],
    isAggregate: false,
    isGrouped: false,
  };
}
